import { PayerRow } from '@finpay-development/shared-platform/lib/esm/admin-configuration/models/payers';
import { VobSource } from '@finpay-development/shared-platform/lib/esm/meta-data/models/metaData';

export type VerifyPayerCandidate = {
  payorId: number;
  payorName: string;
  pcode: string;
  isSelected: boolean;
};

/** Normalize payor names for matching (case, spaces, healthcare spelling). */
export function normalizePayorName(name: string | null | undefined): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/healthcare/g, 'health care')
    .replace(/\s+/g, ' ');
}

/** Availity payer code (externalCode) for a master payor. */
export function getAvailityPcodeForPayor(
  payorId: number,
  payersMap: Map<number, PayerRow> | null | undefined,
  vobSources: VobSource[] | null | undefined
): string {
  const availityVobSource = (vobSources ?? []).find(
    (source) => source.slug === 'availity'
  );
  if (!availityVobSource) {
    return '';
  }
  const payor = payersMap?.get(payorId);
  const availityPayorSource = payor?.payorSource?.find(
    (source) => source.vobSourceId === availityVobSource.vobSourceId
  );
  return availityPayorSource?.externalCode != null
    ? String(availityPayorSource.externalCode).trim()
    : '';
}

function normalizeConfiguredPayorIds(
  configuredPayorIds: number[] | null | undefined
): number[] {
  if (!Array.isArray(configuredPayorIds)) {
    return [];
  }
  const seen = new Set<number>();
  const ids: number[] = [];
  for (const raw of configuredPayorIds) {
    const payorId = Number(raw);
    if (!Number.isInteger(payorId) || payorId <= 0 || seen.has(payorId)) {
      continue;
    }
    seen.add(payorId);
    ids.push(payorId);
  }
  return ids;
}

/**
 * Build unique Verify candidates from configured cfg payor ids + selected if Availity-coded.
 * Configured ids without an Availity pcode are skipped; selected without pcode is skipped
 * but configured payors still fan out.
 */
export function buildVerifyPayerCandidates(
  selectedPayorId: number,
  configuredPayorIds: number[] | null | undefined,
  payersMap: Map<number, PayerRow> | null | undefined,
  vobSources: VobSource[] | null | undefined
): VerifyPayerCandidate[] {
  const byPayorId = new Map<number, VerifyPayerCandidate>();

  for (const payorId of normalizeConfiguredPayorIds(configuredPayorIds)) {
    const pcode = getAvailityPcodeForPayor(payorId, payersMap, vobSources);
    if (!pcode) {
      continue;
    }
    const payor = payersMap?.get(payorId);
    byPayorId.set(payorId, {
      payorId,
      payorName: payor?.payorName ?? '',
      pcode,
      isSelected: payorId === selectedPayorId,
    });
  }

  if (selectedPayorId > 0 && !byPayorId.has(selectedPayorId)) {
    const pcode = getAvailityPcodeForPayor(
      selectedPayorId,
      payersMap,
      vobSources
    );
    if (pcode) {
      const selected = payersMap?.get(selectedPayorId);
      byPayorId.set(selectedPayorId, {
        payorId: selectedPayorId,
        payorName: selected?.payorName ?? '',
        pcode,
        isSelected: true,
      });
    }
  } else if (byPayorId.has(selectedPayorId)) {
    const existing = byPayorId.get(selectedPayorId)!;
    byPayorId.set(selectedPayorId, { ...existing, isSelected: true });
  }

  return Array.from(byPayorId.values());
}
